import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import {
  fetchUserWatchlist,
  fetchMovieDetailsBatch,
  fetchEpisodeDetails,
  fetchTvStoreCategories,
  getImageUrl,
} from "../../redux/content";
import { getTvodProductMap, buildMovieDetailNavState } from "../../utils/tvodMap";
import "../../components/styles/watchlistPage.scss";

/**
 * WatchlistPage — Figma: 24654:5408 (Desktop), 24654:8833 (Mobile)
 * "Watchlist" heading + landscape image-only cards (416×246) in wrapping grid
 *
 * Data flow:
 *   1. fetchUserWatchlist() → Redux watchlist (tagged movie/episode items)
 *   2. fetchMovieDetailsBatch(movieIds) → image_store_id per movie
 *   3. fetchEpisodeDetails(episodeIds) → series_id + images per episode
 *   4. Build displayItems: [{ id, navigateId, imageUrl }]
 *   5. Render landscape image-only cards — no titles (not in Figma, not in API)
 */

const WatchlistPage = () => {
  const navigate = useNavigate();
  const { watchlist, loaders, tvStoreCategories } = useSelector((state) => state.content);
  const [displayItems, setDisplayItems] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const fetchedRef = useRef(false);
  const tvodMapRef = useRef(new Map());

  // Fetch watchlist + TV Store categories on mount (guarded against StrictMode double-fire)
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchUserWatchlist();
      if (tvStoreCategories.length === 0) fetchTvStoreCategories();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load TVOD product map for rental price detection on nav
  useEffect(() => {
    if (tvStoreCategories.length > 0) {
      getTvodProductMap(tvStoreCategories).then((map) => { tvodMapRef.current = map; });
    }
  }, [tvStoreCategories]);

  // When watchlist updates, fetch details to resolve images.
  // Use a generation counter to prevent stale async writes from earlier runs.
  const detailGenRef = useRef(0);

  useEffect(() => {
    if (watchlist.length === 0) {
      setDisplayItems([]);
      return;
    }

    const gen = ++detailGenRef.current;

    const loadDetails = async () => {
      setDetailsLoading(true);

      // Deduplicate by movie_id / episode_id — API can return duplicate bookmarks
      const seenMovies = new Set();
      const seenEpisodes = new Set();
      const movieItems = watchlist.filter((item) => {
        if (item._type !== "movie") return false;
        if (seenMovies.has(item.movie_id)) return false;
        seenMovies.add(item.movie_id);
        return true;
      });
      const episodeItems = watchlist.filter((item) => {
        if (item._type !== "episode") return false;
        if (seenEpisodes.has(item.episode_id)) return false;
        seenEpisodes.add(item.episode_id);
        return true;
      });

      console.log("[WatchlistPage] Resolving details — movies:", movieItems.length, "episodes:", episodeItems.length, "gen:", gen);

      // Fetch movie details in parallel with episode details
      const [movieDetails, episodeDetailMap] = await Promise.all([
        movieItems.length > 0
          ? fetchMovieDetailsBatch(movieItems.map((b) => ({ id: b.movie_id })))
          : Promise.resolve([]),
        episodeItems.length > 0
          ? fetchEpisodeDetails(episodeItems.map((b) => b.episode_id))
          : Promise.resolve({}),
      ]);

      // Stale effect guard — only the latest generation should write to state
      if (gen !== detailGenRef.current) return;

      // Build unified display items
      const items = [];

      // Movies — image from image_store_id, carry detail for TVOD detection on click
      movieItems.forEach((bookmark) => {
        const detail = movieDetails.find((m) => m.id === bookmark.movie_id);
        const imageUrl = detail
          ? getImageUrl(detail, "POSTER") || getImageUrl(detail, "BANNER")
          : null;
        items.push({
          id: `movie-${bookmark.movie_id}`,
          navigateId: bookmark.movie_id,
          imageUrl,
          movie: detail, // carries uid + packages for buildMovieDetailNavState
        });
      });

      // Episodes — image from episode images, navigate to parent series (always Discover)
      episodeItems.forEach((bookmark) => {
        const detail = episodeDetailMap[bookmark.episode_id];
        const imageUrl = detail?.images
          ? getImageUrl({ image_id: detail.images.POSTER || detail.images.PREVIEW })
          : null;
        const navigateId = detail?.series_id || bookmark.episode_id;
        items.push({
          id: `episode-${bookmark.episode_id}`,
          navigateId,
          imageUrl,
          movie: null, // episodes → always Discover tab
        });
      });

      console.log("[WatchlistPage] Display items ready:", items.length, "gen:", gen);
      setDisplayItems(items);
      setDetailsLoading(false);
    };

    loadDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist]);

  const isLoading = loaders.watchlist || detailsLoading;
  const isEmpty = !isLoading && displayItems.length === 0;

  console.log("[WatchlistPage] rendering — items:", displayItems.length, "loading:", isLoading, "empty:", isEmpty);

  return (
    <div className="watchlist-page">
      <Navbar activeTab="" />

      <div className="watchlist-page__body">
        <h1 className="watchlist-page__title">Watchlist</h1>

        {/* Loading skeletons */}
        {isLoading && (
          <div className="watchlist-page__grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={`skel-${i}`}
                className="watchlist-page__card watchlist-page__card--skeleton"
              >
                <div className="watchlist-page__skeleton-shimmer" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="watchlist-page__empty">
            <p className="watchlist-page__empty-text">Your watchlist is empty</p>
            <button
              className="watchlist-page__empty-btn"
              onClick={() => navigate("/home")}
            >
              Browse Content
            </button>
          </div>
        )}

        {/* Image-only cards — no titles per Figma */}
        {!isLoading && displayItems.length > 0 && (
          <div className="watchlist-page__grid">
            {displayItems.map((item) => (
              <div
                key={item.id}
                className="watchlist-page__card"
                onClick={() => {
                  const navState = item.movie ? buildMovieDetailNavState(item.movie, tvodMapRef.current) : {};
                  console.log("[WatchlistPage] Card clicked, navigating to:", item.navigateId, "state:", navState);
                  navigate(`/details/${item.navigateId}`, { state: navState });
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const navState = item.movie ? buildMovieDetailNavState(item.movie, tvodMapRef.current) : {};
                    navigate(`/details/${item.navigateId}`, { state: navState });
                  }
                }}
              >
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt=""
                    className="watchlist-page__card-img"
                  />
                ) : (
                  <div className="watchlist-page__card-placeholder" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default WatchlistPage;
